import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  adminDeleteComment,
  adminDeleteOrganization,
  adminDeletePost,
  adminDeleteUser,
  adminGetOrganization,
  adminGetOrganizationComments,
  adminGetOrganizationPosts,
  adminGetPost,
  adminGetUser,
  adminGetUserComments,
  adminGetUserPosts,
} from "../../api/api";
import { AdminModerationProvider } from "../../context/AdminModerationContext";
import { defaultPfpUrl, formatRelativeTimestamp } from "../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { FormattedBody } from "../ui/formatted-body";
import { Separator } from "../ui/separator";
import { Skeleton } from "../ui/skeleton";
import {
  NotificationEmptyState,
  NotificationErrorPanel,
} from "../notifications_page/NotificationTabShared";
import ContentOverflowMenu from "../feed_page/ContentOverflowMenu";
import PostDetailModal from "../profile_page/PostDetailModal";
import PostPreviewCard from "../profile_page/PostPreviewCard";
import AdminModerationDeleteDialog from "./AdminModerationDeleteDialog";
import { formatUserVerification } from "./AdminPlaceholders";

/**
 * Initials for an avatar fallback.
 * @param {string} name
 * @returns {string}
 */
function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

/**
 * Platform-admin profile: identity, posts, comments, and moderation delete.
 *
 * @param {Object} props
 * @param {number} props.adminId
 * @param {"users"|"organizations"} props.mode
 * @returns {JSX.Element}
 */
export default function AdminProfileDetail({ adminId, mode }) {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const isOrg = mode === "organizations";
  const targetId = Number(accountId);
  const listPath = isOrg ? "/platform-admin/organizations" : "/platform-admin/users";

  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedPostId, setSelectedPostId] = useState(null);
  const [focusCommentId, setFocusCommentId] = useState(null);

  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const [confirmAccountDelete, setConfirmAccountDelete] = useState(false);
  const [accountDeleting, setAccountDeleting] = useState(false);

  const loadProfile = useCallback(async () => {
    if (!adminId || !Number.isFinite(targetId) || targetId <= 0) {
      setError("Invalid profile");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    const [profileResult, postsResult, commentsResult] = await Promise.all([
      isOrg ? adminGetOrganization(adminId, targetId) : adminGetUser(adminId, targetId),
      isOrg ? adminGetOrganizationPosts(adminId, targetId) : adminGetUserPosts(adminId, targetId),
      isOrg
        ? adminGetOrganizationComments(adminId, targetId)
        : adminGetUserComments(adminId, targetId),
    ]);
    if (profileResult.error || profileResult.success === false) {
      setError(profileResult.error || profileResult.message || "Profile not found");
      setProfile(null);
      setPosts([]);
      setComments([]);
      setLoading(false);
      return;
    }
    setProfile(profileResult.data || null);
    setPosts(Array.isArray(postsResult.data) ? postsResult.data : []);
    setComments(Array.isArray(commentsResult.data) ? commentsResult.data : []);
    if (postsResult.error) {
      setError(postsResult.error);
    } else if (commentsResult.error) {
      setError(commentsResult.error);
    }
    setLoading(false);
  }, [adminId, isOrg, targetId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    setSelectedPostId(null);
    setFocusCommentId(null);
    setPendingDelete(null);
    setConfirmAccountDelete(false);
    setDeleteError("");
  }, [targetId]);

  /**
   * Open the moderation delete dialog.
   * @param {{contentType: string, item: object, onSuccess?: function}} request
   */
  const requestDelete = useCallback((request) => {
    setPendingDelete(request);
    setDeleteError("");
  }, []);

  const loadSelectedPost = useCallback(
    (postId) => adminGetPost(adminId, postId),
    [adminId]
  );

  const handlePostOpenChange = useCallback((nextOpen) => {
    if (!nextOpen) {
      setSelectedPostId(null);
      setFocusCommentId(null);
    }
  }, []);

  const handlePostDeleted = useCallback((postId) => {
    setPosts((current) => current.filter((post) => post.id !== postId));
    setComments((current) =>
      current.filter((comment) => Number(comment.post_id) !== Number(postId))
    );
  }, []);

  /**
   * Soft-delete the pending item and notify the author when requested.
   * @param {{notify: boolean, moderatorComment: string}} options
   */
  async function confirmModerationDelete(options) {
    if (!pendingDelete || !adminId) return;
    const { contentType, item, onSuccess } = pendingDelete;
    setDeleteBusy(true);
    setDeleteError("");
    const result =
      contentType === "post"
        ? await adminDeletePost(adminId, item.id, options)
        : await adminDeleteComment(adminId, item.id, options);
    setDeleteBusy(false);
    if (result.error || result.success === false) {
      setDeleteError(result.error || result.message || "Failed to delete");
      return;
    }
    if (contentType === "post") {
      setPosts((current) => current.filter((post) => post.id !== item.id));
      setComments((current) => current.filter((comment) => Number(comment.post_id) !== Number(item.id)));
      if (Number(selectedPostId) === Number(item.id)) {
        setSelectedPostId(null);
        setFocusCommentId(null);
      }
    } else {
      setComments((current) => current.filter((comment) => comment.id !== item.id));
    }
    onSuccess?.();
    setPendingDelete(null);
  }

  async function confirmDeleteAccount() {
    if (!adminId || !profile) return;
    setAccountDeleting(true);
    const result = isOrg
      ? await adminDeleteOrganization(adminId, profile.id)
      : await adminDeleteUser(adminId, profile.id);
    setAccountDeleting(false);
    if (result.error || result.success === false) {
      setError(result.error || result.message || "Failed to delete account");
      setConfirmAccountDelete(false);
      return;
    }
    navigate(listPath, { replace: true });
  }

  const displayName = isOrg
    ? profile?.name || profile?.username || "Organization"
    : `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim() ||
      profile?.username ||
      "User";

  return (
    <AdminModerationProvider onRequestDelete={requestDelete}>
      <div>
        <Link to={listPath} className="mb-4 inline-block text-sm text-accent hover:text-accent-hover">
          Back to {isOrg ? "organizations" : "users"}
        </Link>

        {loading ? (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Skeleton className="h-20 w-20 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-6 w-1/3" />
                <Skeleton className="h-4 w-1/4" />
              </div>
            </div>
            <Skeleton className="h-24 w-full" />
          </div>
        ) : null}

        {!loading && error && !profile ? <NotificationErrorPanel message={error} /> : null}

        {!loading && profile ? (
          <div className="space-y-6">
            {error ? <NotificationErrorPanel message={error} /> : null}

            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 items-start gap-4">
                <Avatar className="h-20 w-20 border-2 border-ui-border shadow-sm">
                  <AvatarImage
                    src={profile.pfp_url || defaultPfpUrl}
                    alt=""
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = defaultPfpUrl;
                    }}
                  />
                  <AvatarFallback className="text-xl font-semibold text-text-secondary">
                    {initials(displayName)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <h2 className="truncate text-2xl font-bold tracking-tight text-text-primary">
                    {displayName}
                  </h2>
                  <p className="truncate text-sm text-text-secondary">@{profile.username}</p>
                  <p className="truncate text-sm text-text-muted">{profile.email}</p>
                  <p className="mt-1 truncate text-xs uppercase tracking-wide text-text-muted">
                    {isOrg ? `Status: ${profile.status}` : formatUserVerification(profile)}
                  </p>
                  {profile.location ? (
                    <p className="mt-1 text-sm text-text-muted">{profile.location}</p>
                  ) : null}
                </div>
              </div>
              <Button
                type="button"
                variant="destructive"
                className="shrink-0"
                onClick={() => setConfirmAccountDelete(true)}
              >
                Delete account
              </Button>
            </div>

            <Card className="shadow-profile-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-text-primary">About</CardTitle>
                <Separator />
              </CardHeader>
              <CardContent>
                <FormattedBody as="p" className="text-text-secondary">
                  {profile.bio || "No bio available."}
                </FormattedBody>
              </CardContent>
            </Card>

            <Card className="shadow-profile-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-text-primary">
                  {isOrg ? "People" : "Organizations"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profile.affiliations && profile.affiliations.length > 0 ? (
                  <ul className="space-y-2">
                    {profile.affiliations
                      .filter((affiliation) => affiliation.status === "active")
                      .map((affiliation) => {
                        if (isOrg) {
                          const person = affiliation.user || {};
                          const personName =
                            `${person.first_name || ""} ${person.last_name || ""}`.trim() ||
                            person.username ||
                            `User #${affiliation.user_id}`;
                          return (
                            <li key={affiliation.id}>
                              <Link
                                to={`/platform-admin/users/${person.id || affiliation.user_id}`}
                                className="text-sm font-medium text-accent hover:text-accent-hover"
                              >
                                {personName}
                              </Link>
                              {affiliation.role ? (
                                <span className="text-sm text-text-muted"> · {affiliation.role}</span>
                              ) : null}
                            </li>
                          );
                        }
                        const organization = affiliation.organization || {};
                        return (
                          <li key={affiliation.id}>
                            <Link
                              to={`/platform-admin/organizations/${organization.id || affiliation.org_id}`}
                              className="text-sm font-medium text-accent hover:text-accent-hover"
                            >
                              {organization.name || organization.username || "Organization"}
                            </Link>
                            {affiliation.role ? (
                              <span className="text-sm text-text-muted"> · {affiliation.role}</span>
                            ) : null}
                          </li>
                        );
                      })}
                  </ul>
                ) : (
                  <p className="text-sm text-text-muted">
                    {isOrg ? "No people listed." : "No organizations found."}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-profile-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-text-primary">Posts</CardTitle>
                <Separator />
              </CardHeader>
              <CardContent>
                {posts.length === 0 ? (
                  <NotificationEmptyState
                    title="No posts"
                    subtitle="This profile has not published any posts."
                    className="border-solid bg-ui-card"
                  />
                ) : (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {posts.map((post) => (
                      <div key={post.id} className="relative">
                        <PostPreviewCard
                          post={post}
                          onSelect={(postId) => {
                            setFocusCommentId(null);
                            setSelectedPostId(postId);
                          }}
                        />
                        <div
                          className="absolute right-1 top-1 z-10"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onKeyDown={(event) => event.stopPropagation()}
                        >
                          <ContentOverflowMenu
                            userId={0}
                            user={null}
                            item={post}
                            contentType="post"
                            triggerClassName="bg-ui-card/90 text-text-primary shadow-sm hover:bg-ui-card"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-profile-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-text-primary">Comments</CardTitle>
                <Separator />
              </CardHeader>
              <CardContent>
                {comments.length === 0 ? (
                  <NotificationEmptyState
                    title="No comments"
                    subtitle="This profile has not posted any comments or replies."
                    className="border-solid bg-ui-card"
                  />
                ) : (
                  <ul className="space-y-3" aria-label="Comments">
                    {comments.map((comment) => {
                      const isReply = Number(comment.parent_id) > 0;
                      return (
                        <li key={comment.id}>
                          <div className="rounded-md border border-ui-border bg-ui-card p-3">
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                              <p className="text-xs uppercase tracking-wide text-text-muted">
                                {isReply ? "Reply" : "Comment"}
                                {comment.created_at
                                  ? ` · ${formatRelativeTimestamp(comment.created_at)}`
                                  : ""}
                              </p>
                              <ContentOverflowMenu
                                userId={0}
                                user={null}
                                item={comment}
                                contentType={isReply ? "reply" : "comment"}
                              />
                            </div>
                            <button
                              type="button"
                              className="w-full text-left"
                              onClick={() => {
                                setFocusCommentId(comment.id);
                                setSelectedPostId(comment.post_id);
                              }}
                            >
                              <FormattedBody
                                className="line-clamp-4 text-sm text-text-primary"
                                mentions={comment.mentions}
                              >
                                {comment.content}
                              </FormattedBody>
                              {comment.post_caption ? (
                                <p className="mt-2 line-clamp-2 text-xs text-text-muted">
                                  On post: {comment.post_caption}
                                </p>
                              ) : null}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        ) : null}

        <PostDetailModal
          open={selectedPostId != null}
          onOpenChange={handlePostOpenChange}
          postId={selectedPostId}
          userId={0}
          user={null}
          focusCommentId={focusCommentId}
          loadPost={loadSelectedPost}
          onDeleted={handlePostDeleted}
        />

        <AdminModerationDeleteDialog
          open={!!pendingDelete}
          onOpenChange={(nextOpen) => {
            if (!nextOpen && !deleteBusy) setPendingDelete(null);
          }}
          contentType={pendingDelete?.contentType || "post"}
          busy={deleteBusy}
          error={deleteError}
          onConfirm={confirmModerationDelete}
        />

        <Dialog open={confirmAccountDelete} onOpenChange={setConfirmAccountDelete}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete this account?</DialogTitle>
              <DialogDescription className="space-y-2 text-text-secondary">
                <span className="block text-text-primary">
                  {displayName} (@{profile?.username})
                </span>
                <span className="block">
                  This permanently removes the account and related affiliations, connections, and
                  chats. This cannot be undone.
                </span>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmAccountDelete(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={accountDeleting}
                onClick={confirmDeleteAccount}
              >
                {accountDeleting ? "Deleting…" : "Delete permanently"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminModerationProvider>
  );
}
